import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { TWallExtremePointHostType, WallExtremePointEntity } from '@/editor/entities/wallExtremePointEntity';
import { OperateAbstract } from './OperateAbstract';
import {
  EEntityType,
  EExtremePointType,
  EWallOperateType,
  IOriginModifyWallInfo,
  IUniformWallData
} from '@/editor/types';
import VirtualWallModel, { EVirtualWallStatus, EVirtualWallStyle } from '@/editor/entities/2d/virtualWallModel';
import { isBoolean } from 'lodash-es';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import { DEFAULT_RULER_OFFSET } from '@/editor/constant/ruler';
import { getArcWallRulerInfos } from '@/editor/utils/ruler/wall';
import { isModifyWallsLegal } from '@/editor/stores/utils/legality/sepWall';
import { convertSepWallTypeToVirtualWallStyle } from '@/editor/utils/wall';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { WallMoveCalculator } from '@/editor/utils/wall/wallMoveCalculator';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { MAX_CHORD_LENGTH, MAX_SAGITTA, MIN_SAGITTA } from '@/editor/constant/wall';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { FloatingPoint } from '@/editor/math/number';

type TVirtualWallData = IUniformWallData & {
  style?: EVirtualWallStyle;
  status?: EVirtualWallStatus;
};

export class OperateWallPoint extends OperateAbstract<EEntityType.WallExtremePoint> {
  private _checkedEntity: WallExtremePointEntity | null = null;
  private _mainWallId: string = '';
  private _mainWallType: TWallExtremePointHostType = EEntityType.Wall;
  private _mainWallPointType: EExtremePointType = EExtremePointType.StartPoint;
  private _rulerModel = new RulerModel();
  private _virtualWallModel = new VirtualWallModel();
  private _wallPanCalculate = globalStores.wallOperation.wallPan;
  private _movePoint: IPoint2d | null = null;
  private _separateWallPanCalculate = new WallMoveCalculator(globalStores.separateWallStore);

  private _wallAbsorbManager = new WallPointAbsorbManager(globalStores.wallStore);
  private _separateWallAbsorbManager = new WallPointAbsorbManager(
    globalStores.separateWallStore,
    EEntityType.SeparateWall
  );

  constructor() {
    super();
  }

  private get wallPanCalculate() {
    return this._mainWallType === EEntityType.SeparateWall ? this._separateWallPanCalculate : this._wallPanCalculate;
  }

  private get absorbManager() {
    return this._mainWallType === EEntityType.SeparateWall ? this._separateWallAbsorbManager : this._wallAbsorbManager;
  }

  set checkedEntity(wallExtremePoint: WallExtremePointEntity | null) {
    if (this._checkedEntity) {
      this._checkedEntity.selected = false;
    }

    this._checkedEntity = wallExtremePoint;
    if (!this._checkedEntity) {
      return;
    }

    const { wallId, wallType, pointType } = this._checkedEntity.getData();
    this._mainWallId = wallId;
    this._mainWallType = wallType;
    this._mainWallPointType = pointType;
    this._checkedEntity.selected = true; // TODO: 优化
    this.wallPanCalculate.setMainWall(this._mainWallId);
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  override startMove(point: IPoint2d): void {
    const wallData = this.getMainWallData();
    if (!wallData) return;

    this._movePoint = this._checkedEntity?.position || point;
    this.updateModel([wallData]);
    editorEvent.emit(EditorEventName.REFRESH_SELECTED);

    // 吸附设置
    if (this._mainWallType === EEntityType.SeparateWall) {
      const separateWallData = globalStores.separateWallStore.getSepData(this._mainWallId, false);
      this._separateWallAbsorbManager.lockSeparateWallRoom(separateWallData?.relateRoomId);
    } else {
      this.absorbManager.updateWallData();
    }

    if (this._mainWallPointType === EExtremePointType.StartPoint) {
      this.absorbManager.setOrthogonalData(wallData.end);
    } else if (this._mainWallPointType === EExtremePointType.EndPoint) {
      this.absorbManager.setOrthogonalData(wallData.start);
    } else {
      this.absorbManager.clearOrthogonalData();
    }

    this._movePoint = null;
  }

  completeMove() {
    if (!this._checkedEntity || !this._movePoint) return;

    const mainWallData = this.getMainWallData();

    if (!mainWallData) {
      this.clear();
      return;
    }

    const operateType = this.getOperateType();
    if (!operateType) {
      this.clear();
      return;
    }

    const result = this.commitWallPointMove(operateType, this._movePoint);

    void Promise.resolve(result).then(() => {
      this.clear();
      editorEvent.emit(EditorEventName.REFRESH_SELECTED);
    });
  }

  move(point: IPoint2d) {
    if (!this._checkedEntity || !this._mainWallId) return point;

    const mainWallData = this.getMainWallData();
    if (!mainWallData) return point;

    let absorbPoint: IPoint2d | undefined;
    if (this._checkedEntity.getData().pointType === EExtremePointType.ArcMidPoint) {
      if (mainWallData.center) {
        const { start: startPoint, end: endPoint } = mainWallData;
        absorbPoint = this.absorbManager.calculateArcAbsorbPointThenRender({
          arcStart: startPoint,
          arcEnd: endPoint,
          rawPoint: point
        });

        const sagitta = ArcSeg2d.calculateSagitta(startPoint, endPoint, absorbPoint ? absorbPoint : point);
        if (Math.abs(sagitta) < MIN_SAGITTA) {
          const arc = new ArcSeg2d({
            start: startPoint,
            end: endPoint,
            sagitta: MIN_SAGITTA * Math.sign(sagitta)
          });
          absorbPoint = arc.getMidPoint();
        } else if (Math.abs(sagitta) > MAX_SAGITTA) {
          const arc = new ArcSeg2d({
            start: startPoint,
            end: endPoint,
            sagitta: MAX_SAGITTA * Math.sign(sagitta)
          });
          absorbPoint = arc.getMidPoint();
          this.absorbManager.clearModel();
        }
      }
    } else {
      absorbPoint = this.absorbManager.calculateAbsorbPointThenRender(point);

      if (mainWallData.center) {
        const fixedPoint =
          this._mainWallPointType === EExtremePointType.StartPoint ? mainWallData.end : mainWallData.start;
        const candidate = absorbPoint ?? point;
        const chordLength = Point2dUtils.distanceTo(fixedPoint, candidate);
        if (FloatingPoint.greaterThan(chordLength, MAX_CHORD_LENGTH)) {
          absorbPoint = Point2dUtils.addDistance(
            fixedPoint,
            MAX_CHORD_LENGTH,
            Point2dUtils.getDirection(fixedPoint, candidate)
          );
          this.absorbManager.clearModel();
        }
      }
    }

    if (absorbPoint) point = absorbPoint;

    // 关联墙体计算
    const operateType = this.getOperateType();
    if (!operateType) return point;

    const result = this.wallPanCalculate.compute({ type: operateType, point });
    const modifyWallInfos = result.modifyWallInfos || [];
    const isIllegal =
      this._mainWallType === EEntityType.SeparateWall && !isModifyWallsLegal({ modifyWallInfos }).isLegal;

    if (isIllegal) {
      this._separateWallAbsorbManager.clearModel();
    }
    const status = isIllegal ? EVirtualWallStatus.Unnormal : EVirtualWallStatus.Normal;
    const virtualWallDatas = modifyWallInfos
      .map((info) => this.getVirtualWallData(info, status))
      .filter((info): info is TVirtualWallData => !!info);

    this.updateModel(virtualWallDatas);

    this._movePoint = point;

    return point;
  }

  private getOperateType():
    | EWallOperateType.StartPoint
    | EWallOperateType.EndPoint
    | EWallOperateType.ArcMidPoint
    | null {
    return this._mainWallPointType === EExtremePointType.ArcMidPoint
      ? EWallOperateType.ArcMidPoint
      : this._mainWallPointType === EExtremePointType.StartPoint
        ? EWallOperateType.StartPoint
        : EWallOperateType.EndPoint;
  }

  private commitWallPointMove(
    operateType: EWallOperateType.StartPoint | EWallOperateType.EndPoint | EWallOperateType.ArcMidPoint,
    point: IPoint2d
  ) {
    if (this._mainWallType === EEntityType.SeparateWall) {
      return globalStores.separateWallOperation.moveSeparateWallPoint(this._mainWallId, {
        type: operateType,
        point
      });
    }

    return globalStores.wallOperation.moveWallPoint(this._mainWallId, {
      type: operateType,
      point
    });
  }

  private getMainWallData(): TVirtualWallData | null {
    if (this._mainWallType === EEntityType.SeparateWall) {
      const separateWallData = globalStores.separateWallStore.getSepData(this._mainWallId, false);
      if (!separateWallData) return null;

      return {
        id: separateWallData.id,
        start: separateWallData.start,
        end: separateWallData.end,
        thickness: separateWallData.thickness,
        center: separateWallData.center,
        clockwise: separateWallData.clockwise,
        style: convertSepWallTypeToVirtualWallStyle(separateWallData.type),
        status: EVirtualWallStatus.Normal
      };
    }

    const wallData = globalStores.wallStore.getWallData(this._mainWallId);
    if (!wallData) return null;

    return { id: wallData.id, ...wallData.baseData };
  }

  private getVirtualWallData(info: IOriginModifyWallInfo, status: EVirtualWallStatus): TVirtualWallData | null {
    if (this._mainWallType === EEntityType.SeparateWall) {
      const wallData = globalStores.separateWallStore.getSepData(info.wallId, false);
      if (!wallData) return null;

      return {
        id: info.wallId,
        start: info.updateBaseData?.start || wallData.start,
        end: info.updateBaseData?.end || wallData.end,
        thickness: info.updateBaseData?.thickness ?? wallData.thickness,
        center: info.updateBaseData?.center || wallData.center,
        clockwise: isBoolean(info.updateBaseData?.clockwise) ? info.updateBaseData?.clockwise : wallData.clockwise,
        style: convertSepWallTypeToVirtualWallStyle(wallData.type),
        status
      };
    }

    const wallData = globalStores.wallStore.getWallData(info.wallId);
    if (!wallData) return null;

    const { start, end, thickness, center, clockwise } = wallData.baseData;
    return {
      id: info.wallId,
      start: info.updateBaseData?.start || start,
      end: info.updateBaseData?.end || end,
      thickness,
      center: info.updateBaseData?.center || center,
      clockwise: isBoolean(info.updateBaseData?.clockwise) ? info.updateBaseData?.clockwise : clockwise
    };
  }

  private updateModel(modifyWallDatas: TVirtualWallData[]) {
    // 更新虚拟墙体
    this._virtualWallModel.update(modifyWallDatas);

    // 更新标尺
    const mainWallData = modifyWallDatas.find((wd) => wd.id === this._mainWallId);
    if (!mainWallData) {
      this._rulerModel.clear();
      return;
    }

    if (!mainWallData.center) {
      this._rulerModel.updateModel([
        {
          id: '0',
          start: mainWallData.start,
          end: mainWallData.end,
          offset: mainWallData.thickness / 2 + DEFAULT_RULER_OFFSET
        }
      ]);
      return;
    }

    const arcRulerInfos = getArcWallRulerInfos(mainWallData as Required<typeof mainWallData>);
    this._rulerModel.updateModel([arcRulerInfos.chordRuler, arcRulerInfos.sagittaRuler]);
  }

  clear() {
    this.checkedEntity = null;
    this._movePoint = null;
    this._virtualWallModel.clear();
    this._rulerModel.clear();
    this._wallAbsorbManager.clearOrthogonalData();
    this._wallAbsorbManager.clearModel();
    this._separateWallAbsorbManager.clearOrthogonalData();
    this._separateWallAbsorbManager.clearModel();
    this._separateWallAbsorbManager.clearSeparateWallRoom();
  }

  dispose() {
    this.clear();

    this._virtualWallModel.dispose();
    this._wallAbsorbManager.dispose();
    this._separateWallAbsorbManager.dispose();
    this._rulerModel.dispose();
  }
}
